import type { ServiceGateway, ServiceResult } from '@ayumia/component-runtime';
import { ApiError, type ApiClient } from './client';

/**
 * Passerelle des actions `api` du runtime de composants (ADR-048 §5) : `command:<Nom>` devient
 * `POST /commands/{Nom}`, `query:<Nom>` devient `GET /queries/{Nom}`. Le serveur décide de tout
 * (authentification, tenant, politique, idempotence, audit) ; un refus revient en erreur typée,
 * que le runtime présente comme `permission_denied`.
 */
export function apiGateway(client: ApiClient): ServiceGateway {
  return {
    async call(kind, name, params): Promise<ServiceResult> {
      try {
        if (kind === 'command') {
          const { target, expected_version, reason, idempotency_key, ...payload } = params as Record<string, unknown>;
          const input: Parameters<ApiClient['command']>[1] = { payload };
          if (isTarget(target)) input.target = target;
          if (typeof expected_version === 'number') input.expected_version = expected_version;
          if (typeof reason === 'string') input.reason = reason;
          if (typeof idempotency_key === 'string') input.idempotency_key = idempotency_key;
          return { ok: true, value: await client.command(name, input) };
        }
        const parameters: Record<string, string> = {};
        for (const [key, value] of Object.entries(params)) if (value !== null && value !== undefined) parameters[key] = String(value);
        return { ok: true, value: (await client.query(name, parameters)).data };
      } catch (error) {
        if (error instanceof ApiError) return { ok: false, error: error.toServiceError() };
        return { ok: false, error: { category: 'INTERNAL_ERROR', code: 'CLIENT_ERROR', message: error instanceof Error ? error.message : String(error) } };
      }
    },
  };
}

function isTarget(value: unknown): value is { type: string; id: string } {
  return typeof value === 'object' && value !== null && typeof (value as { type?: unknown }).type === 'string' && typeof (value as { id?: unknown }).id === 'string';
}
